import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { AsyncPipe, NgClass } from '@angular/common';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { FormsModule } from '@angular/forms';
import { DropdownComponent } from '../../../../../../framework/inputs/dropdown/dropdown.component';
import { viewProjectActions } from '../../../../../../store/view-project/view-project.actions';
import { VIEW_PROJECT_REFRESH_CAUSE } from '../../../../../../framework/constants/view-project.constants';
import { IBudgetTemplate, TemplateId } from '../../../../../../store/templates/templates.types';
import { viewProjectSelectors } from '../../../../../../store/view-project/view-project.selectors';
import {
  getSelectableYears,
  getSelectedSpendType,
  getSelectedYear,
  selectHasCommitments,
  selectHasPrimeLines,
  selectSpendViews,
} from '../../../../../../store/spend/spend.selectors';
import { ProjectDto } from '../../../projects.interface';
import { templateSelectors } from '../../../../../../store/templates/templates.selectors';
import { ProjectApiService } from '../../../../../../services/project-api.service';
import {
  addDefaultLineItem,
  addNewYear,
  saveToBackend,
  setSelectedSpendType,
  setSelectedYear,
  spendActionTypes,
} from '../../../../../../store/spend/spend.actions';
import { CurrentUserService } from '../../../../../../services/current-user.service';
import {
  DELETE_BUDGET_TEMPLATE,
  getDefaultSpendType,
  SPEND_TYPES,
} from '../../../../../../framework/constants/spend.constants';
import { UploadWindowComponent } from '@app/framework/file-handling/upload-window/upload-window.component';
import { TooltipDirective } from '@app/directives/tooltip.directive';
import { primeSelectors } from '@app/store/prime-commitments/prime-commitments.selectors';
import { NotificationV2Service } from '@app/notification-v2.service';

@Component({
  selector: 'app-spend-distribution-header',
  imports: [
    DropdownComponent,
    NgClass,
    AsyncPipe,
    TooltipDirective,
    UploadWindowComponent,
    FormsModule,
  ],
  templateUrl: './spend-distribution-header.component.html',
  styleUrl: './spend-distribution-header.component.scss',
})
export class SpendDistributionHeaderComponent implements OnInit, AfterViewInit, OnDestroy {
  protected readonly store = inject(Store);
  protected readonly notif = inject(NotificationV2Service);
  protected readonly projectApi = inject(ProjectApiService);
  protected readonly changeDetectorRef = inject(ChangeDetectorRef);
  protected readonly userService = inject(CurrentUserService);

  @Output() importBudgetClicked = new EventEmitter<File>();
  @Output() exportBudgetClicked = new EventEmitter<void>();

  protected readonly SPEND_TYPES = SPEND_TYPES;
  readonly DEFAULT_TEMPLATE_NAME = 'Default';
  readonly DEFAULT_TEMPLATE_ID = -1; // 0 is not valid as dropdown does not detect if value is 0, please leave it at -1
  readonly DELETE_BUDGET_TEMPLATE = DELETE_BUDGET_TEMPLATE;
  templateId = signal<TemplateId | null>(null); // can be null, -1 (default template), or any valid template id (> 0)
  ADD_YEAR = -1;
  currentProject: ProjectDto;
  oldTemplateId: TemplateId = null;
  templates: IBudgetTemplate[] = [];
  possibleYearsOptions = [];
  spendViews$: Observable<SPEND_TYPES[]> = of([]);
  selectedBudgetType: SPEND_TYPES = null;

  hasCommitments$ = this.store.select(selectHasCommitments);
  hasPrimeContract$ = this.store.select(primeSelectors.selectHasPrimeContract);
  selectedProject$ = this.store.select(viewProjectSelectors.getSelectedProject);
  templatesManager$ = this.store.select(viewProjectSelectors.getProjectTemplates(false));
  templatesGC$ = this.store.select(templateSelectors.getBudgetTemplates);
  areTemplatesLoading$ = this.store.select(templateSelectors.getIsLoading);
  isDestroyed$ = new Subject<void>();
  possibleYears$ = this.store.select(getSelectableYears);
  selectedSpendType$ = this.store.select(getSelectedSpendType);
  selectedYear$ = this.store.select(getSelectedYear);
  spendAllocationBudgetMissing$ = this.store.select(
    viewProjectSelectors.spendAllocationBudgetMissing,
  );
  // true if the original template has modifications (items added, or removed)
  templateAdjusted$ = this.store.select(viewProjectSelectors.budgetTemplateAdjusted);
  templateDropdownHovered: boolean;
  templateSelectionDisabled$: Observable<boolean> = of(true);
  hasPrimeLines$ = this.store.select(selectHasPrimeLines);
  viewSelectionDisabled$: Observable<boolean> = this.areTemplatesLoading$;

  ngOnInit(): void {
    this.handlePossibleYearsOptions();
    this.handleSpendTypes();
    this.setTemplateSelectionDisabled();
  }

  ngAfterViewInit() {
    if (this.userService.isManager) {
      this.subscribeToManagerTemplates();
    } else if (this.userService.isPremiumSP) {
      this.subscribeToGCTemplates();
    }
  }

  ngOnDestroy(): void {
    this.isDestroyed$.next();
    this.isDestroyed$.complete();
  }

  subscribeToManagerTemplates() {
    combineLatest([this.templatesManager$, this.selectedProject$])
      .pipe(
        takeUntil(this.isDestroyed$),
        filter(([_, project]) => !!project),
      )
      .subscribe(([templates, project]) => {
        // Handle templates
        const defaultTemplate: IBudgetTemplate = {
          name: this.DEFAULT_TEMPLATE_NAME,
          id: this.DEFAULT_TEMPLATE_ID,
          template_items: [],
          is_owner: true,
          is_trashed: false,
        };

        this.templates = [defaultTemplate, ...templates];
        this.currentProject = project;

        if (project?.items?.length === 0 && project.budget_template_id === null) {
          // project doesn't have a budget, in this case user needs to select a template
          this.templateId.set(null);
        } else {
          // user has a template, set template or default template
          this.templateId.set(project?.budget_template_id ?? this.DEFAULT_TEMPLATE_ID);
          this.oldTemplateId = project?.budget_template_id ?? this.DEFAULT_TEMPLATE_ID;
          this.templates.push(this.DELETE_BUDGET_TEMPLATE);
        }

        // if the selected template is deleted, add it to the list, to display its name as selected template
        if (project?.budget_template?.deleted_at) {
          this.templates = [project.budget_template, ...templates];
        }

        this.changeDetectorRef.detectChanges();
      });
  }

  subscribeToGCTemplates() {
    combineLatest([this.templatesGC$, this.selectedProject$])
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe(([templates, project]) => {
        this.templates = templates;
        this.templateId.set(project?.gcp_project_template_id);
        this.oldTemplateId = project?.gcp_project_template_id;

        if (project?.gcp_project_template_id) {
          this.templates.push(this.DELETE_BUDGET_TEMPLATE);
        }

        // if the selected template is deleted, add it to the list, to display its name as selected template
        if (project?.budget_template?.deleted_at) {
          this.templates = [project.budget_template, ...templates];
        }

        this.currentProject = project;

        this.changeDetectorRef.detectChanges();
      });
  }

  exportBudget() {
    this.exportBudgetClicked.emit();
  }

  importBudget(files: File[]) {
    this.importBudgetClicked.emit(files[0]);
  }

  setTemplate(templateId: number) {
    this.spendAllocationBudgetMissing$.pipe(take(1)).subscribe((isMissing) => {
      if (isMissing) {
        this.updateProjectTemplate(templateId);
      } else {
        this.updateProjectTemplateWithConfirmation(templateId);
      }
    });
  }

  updateProjectTemplateWithConfirmation(templateId: number) {
    const notifMessage =
      templateId === this.DELETE_BUDGET_TEMPLATE.id
        ? 'Are you sure you want to delete your current budget? All budget data will be lost.'
        : 'You are about to reset project templates. This change is irreversible. Are you sure you want to continue?';
    this.notif
      .showConfirmDialog(notifMessage)
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe((resetBudget) => {
        if (!resetBudget) {
          this.changeDetectorRef.detectChanges();
          this.templateId.set(this.oldTemplateId);
          this.changeDetectorRef.detectChanges();
          return;
        }

        this.updateProjectTemplate(templateId);
      });
  }

  async updateProjectTemplate(templateId: number) {
    const loadingMessage =
      templateId === this.DELETE_BUDGET_TEMPLATE.id
        ? 'Deleting budget template, please wait...'
        : 'Applying new template, please wait...';
    const hash = this.notif.showLoading(loadingMessage);

    try {
      const updatedProject = await this.projectApi.updateProject(this.getUpdatePayload(templateId));
      this.store.dispatch(spendActionTypes.setShowZeroLines({ showZeroLines: true }));

      if (templateId === this.DELETE_BUDGET_TEMPLATE.id) {
        this.deleteBudget();
        this.notif.close(hash);
        return;
      }

      if (templateId === this.DEFAULT_TEMPLATE_ID && updatedProject.items.length === 0) {
        this.setDefaultTemplate();
        this.notif.close(hash);
        return;
      }

      this.store.dispatch(
        viewProjectActions.refreshNeeded({
          cause: VIEW_PROJECT_REFRESH_CAUSE.SPEND_SAVE,
        }),
      );

      this.templateId.set(templateId);
      this.oldTemplateId = templateId;
      this.notif.close(hash);
      this.notif.showSuccess('Template successfully saved.');
    } catch (error) {
      console.error('Error updating project template', error);
      this.changeDetectorRef.detectChanges();
      this.templateId.set(this.oldTemplateId);
      this.changeDetectorRef.detectChanges();
      this.notif.close(hash);
      this.notif.showError(
        'There was an error applying the new template. ' +
          (error?.message ?? error?.error?.message ?? ''),
      );
    }
  }

  private deleteBudget() {
    this.store.dispatch(spendActionTypes.deleteAllLineItems());
    if (this.oldTemplateId === this.DEFAULT_TEMPLATE_ID) {
      // if old template was default, we need to call this action to actually delete all items
      this.store.dispatch(spendActionTypes.saveToBackend());
    }
    this.store.dispatch(
      viewProjectActions.refreshNeeded({
        cause: VIEW_PROJECT_REFRESH_CAUSE.SPEND_SAVE,
      }),
    );

    this.templateId.set(null);
    this.oldTemplateId = null;

    this.notif.showSuccess('Template successfully deleted.');
  }

  private setDefaultTemplate() {
    this.store.dispatch(addDefaultLineItem());
    this.store.dispatch(saveToBackend());
  }

  getUpdatePayload(selectedTemplateId: TemplateId) {
    const templateId = [this.DEFAULT_TEMPLATE_ID, this.DELETE_BUDGET_TEMPLATE.id].includes(
      selectedTemplateId as number,
    )
      ? null // default template is null on backend
      : selectedTemplateId;

    if (this.userService.isManager) {
      return {
        id: this.currentProject.id,
        start_date: this.currentProject.start_date,
        status: this.currentProject.project_status.id,
        title: this.currentProject.title,
        property_id: this.currentProject.property_id,
        budget_template_id: templateId, // default template is null on backend
      };
    }

    return {
      ...this.currentProject,
      gcp_project_template_id: templateId,
    };
  }

  private handlePossibleYearsOptions() {
    this.possibleYears$.pipe(takeUntil(this.isDestroyed$)).subscribe((years) => {
      this.possibleYearsOptions = years.map((year) => {
        return {
          key: year,
          value: year,
        };
      });
      this.possibleYearsOptions.push({
        key: 'Add Year',
        value: this.ADD_YEAR,
      });
    });
  }

  private handleSpendTypes() {
    this.userService.data$.pipe(takeUntil(this.isDestroyed$)).subscribe((user) => {
      this.spendViews$ = this.store.select(selectSpendViews(user.isManager));
      this.selectedBudgetType = getDefaultSpendType(user.isManager);
      this.store.dispatch(setSelectedSpendType({ spendType: this.selectedBudgetType }));
    });

    this.selectedSpendType$.pipe(takeUntil(this.isDestroyed$)).subscribe((type) => {
      this.selectedBudgetType = type;
    });
  }

  private setTemplateSelectionDisabled() {
    this.templateSelectionDisabled$ = combineLatest([
      this.hasCommitments$,
      this.hasPrimeContract$,
      this.selectedSpendType$,
      this.areTemplatesLoading$,
    ]).pipe(
      map(([hasACommitment, hasPrimeContract, selectedBudgetType, templatesLoading]) => {
        const correctViewSelected = [
          SPEND_TYPES.BUDGET,
          SPEND_TYPES.BUDGET_PARENT_CHILD,
          SPEND_TYPES.BUDGET_PARENT_ONLY,
        ].includes(selectedBudgetType);
        return hasACommitment || hasPrimeContract || !correctViewSelected || templatesLoading;
      }),
    );
  }

  setSelectedYear(value: number) {
    if (value !== this.ADD_YEAR) {
      this.store.dispatch(setSelectedYear({ year: value }));
      return;
    }
    this.store.dispatch(addNewYear());
  }

  setBudgetType(value: SPEND_TYPES) {
    this.store.dispatch(setSelectedSpendType({ spendType: value }));
  }
}
